import * as React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Box,
  IconButton,
  Typography,
  Modal,
  Button,
  Divider
} from "@mui/material";
import { ReactComponent as EditIcon } from "../../assets/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/delete.svg";
import { ReactComponent as DollarIcon } from "../../assets/dollar.svg";
import { ReactComponent as DiaryIcon } from "../../assets/diary.svg";
import { ReactComponent as StreaksIcon } from "../../assets/streak.svg";
import { ReactComponent as ChartIcon } from "../../assets/chart.svg";
import { Avatar, Chip } from "@mui/material";
import getInitials from "../../utils/getInitials";
import BadgeDisplay from "../Extra/BadgeDisplay";
import StatsListModal from "../Modals/StatsModal";
import NutritionTrackingModal from "../Modals/DiaryDisplay";
import { useDispatch, useSelector } from "react-redux";
import {
  setLimit,
  setPage,
  deleteMasterclassTraining,
  getAllMasterclassTrainings
} from "../../features/training/trainingSlice";
import { fetchAllUsers } from "../../features/users/userSlice"; // Add this import
import AudioPlayer from "../Extra/AudioPlayer";
import {
  selectMasterclassTrainings,
  selectTotalMasterclassTrainings,
  selectCurrentPage,
  selectLimit
} from "../../features/training/trainingSelectors";
import {
  selectAllUsers,
  selectTotalUsers,
  selectUsersLoading,
  selectUsersError
} from "../../features/users/userSelectors"; // Add this import
import useSignedUrl from "../../utils/useSignedUrl";
import { Close } from "@mui/icons-material";
import { useState } from "react";
import dayjs from "dayjs";

// Custom CSS styles
const styles = {
  tableContainer: {
    borderRadius: "10px 10px 0 0",
    "&::-webkit-scrollbar": {
      width: "4px"
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#374151",
      borderRadius: "4px"
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f1f1"
    }
  },
  headerCell: {
    minWidth: 100,
    backgroundColor: "#374151",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 700
  },
  tableRow: {
    fontSize: "12px"
  }
};

const trainingColumns = [
  { id: "day", label: "Day", align: "left", width: 120 },
  { id: "audio", label: "Audio", align: "left", width: 150 },
  { id: "title", label: "Title", align: "left", minWidth: 140 },
  { id: "actions", label: "", align: "center", minWidth: 130 }
];

const AudioItem = ({ audio }) => {
  const src = useSignedUrl(
    audio,
    process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
  );
  return <AudioPlayer src={src} />;
};

const MasterclassTrainingTable = ({
  selectedMasterclass,
  selectedTrainingType,
  setIsAddingMasterclassTraining,
  setEditData
}) => {
  const [page, setNewPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [userPage, setUserPage] = React.useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = React.useState(10);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(null);
  const [openStats, setOpenStats] = React.useState({}); // Modal state
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const dispatch = useDispatch();

  const masterclassTrainings = useSelector(selectMasterclassTrainings) || [];
  const totalCount = useSelector(selectTotalMasterclassTrainings) || 0;
  const pageNumber = useSelector(selectCurrentPage);
  const limit = useSelector(selectLimit);

  // Users data
  const users = useSelector(selectAllUsers) || [];
  const totalUsers = useSelector(selectTotalUsers) || 0;
  const usersLoading = useSelector(selectUsersLoading);
  const usersError = useSelector(selectUsersError);

  const userColumns = React.useMemo(
    () => [
      { id: "fullName", label: "Name", minWidth: 120, align: "center" },
      { id: "email", label: "Email", minWidth: 120, align: "center" },
      { id: "userType", label: "Role", minWidth: 60, align: "center" },
      {
        id: "trainingCompletions",
        label:
          selectedMasterclass?.type === "coaches"
            ? "10P / Vault / P1 / P2 / P3 (%)"
            : "10P / P1 / P2 / P3 (%)",
        maxWidth: 150,
        align: "center"
      },
      { id: "createdAt", label: "Joined", minWidth: 60, align: "center" },
      {
        id: "lastLogin",
        label: "Last Diary Log",
        minWidth: 70,
        align: "center"
      },
      { id: "streak", label: "Streak", minWidth: 50, align: "center" },
      // { id: "badges", label: "Badges", minWidth: 110, align: "center" },
      { id: "isActive", label: "Status", align: "left" },
      { id: "actions", minWidth: 130, align: "center" }
    ],
    [selectedMasterclass]
  );

  const ImageItem = ({ img }) => {
    const imgSrc = useSignedUrl(
      img,
      process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
    );

    return (
      <Box
        component="img"
        borderRadius="50%"
        height={40}
        width={40}
        src={imgSrc}
        alt="avatar"
      />
    );
  };

  const getRoleChipStyle = (role) => {
    switch (role) {
      case "Gym":
        return {
          style: {
            backgroundColor: "#D7F0DE",
            fontSize: "12px",
            color: "#38B65B"
          },
          label: "Gym"
        };
      case "user":
      case "Affiliate":
        return {
          style: {
            backgroundColor: "#FCECD3",
            fontSize: "12px",
            color: "#F1B44C"
          },
          label: "User"
        };
      case "admin":
        return {
          style: {
            backgroundColor: "#D7F0DE",
            fontSize: "12px",
            color: "#38B65B"
          },
          label: "Admin"
        };
      case "Coach":
        return {
          style: {
            backgroundColor: "#FDD9CD",
            fontSize: "12px",
            color: "#F6541C"
          },
          label: "Coach"
        };
      case "Company":
        return {
          style: {
            backgroundColor: "#D4E9FC",
            fontSize: "12px",
            color: "#368AD0"
          },
          label: "Company"
        };
      default:
        return { style: {} };
    }
  };

  // Fetch users when component mounts or masterclass changes
  React.useEffect(() => {
    if (selectedMasterclass?._id) {
      dispatch(
        fetchAllUsers({
          searchValue: "",
          page: userPage,
          limit: userRowsPerPage,
          date: dayjs().format("YYYY-MM-DD"),
          masterclassId: selectedMasterclass._id
        })
      );
    }
  }, [dispatch, selectedMasterclass?._id, userPage, userRowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setNewPage(newPage);
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setNewPage(0);
    dispatch(setPage(0));
    dispatch(setLimit(+event.target.value));
  };

  const handleUserChangePage = (event, newPage) => {
    setUserPage(newPage);
    dispatch(
      fetchAllUsers({
        page: newPage,
        limit: userRowsPerPage,
        date: dayjs().format("YYYY-MM-DD"),
        masterclassId: selectedMasterclass._id
      })
    );
  };

  const handleUserChangeRowsPerPage = (event) => {
    const newLimit = +event.target.value;
    setUserRowsPerPage(newLimit);
    setUserPage(0);
    dispatch(
      fetchAllUsers({
        page: 0,
        limit: newLimit,
        date: dayjs().format("YYYY-MM-DD"),
        masterclassId: selectedMasterclass._id
      })
    );
  };

  const handleDelete = async (id) => {
    await dispatch(deleteMasterclassTraining(id));
    setConfirmDeleteModal(null);
    // Refresh the list
    dispatch(
      getAllMasterclassTrainings({
        masterclassId: selectedMasterclass._id,
        type: selectedTrainingType,
        page: pageNumber,
        limit
      })
    );
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} color="#374151" mb={3}>
        {selectedTrainingType === "10Principles"
          ? "10 Principles"
          : selectedTrainingType === "CoachesVault"
          ? "Coaches Vault"
          : selectedTrainingType}{" "}
        Trainings
      </Typography>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={styles.tableContainer}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {trainingColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      ...styles.headerCell,
                      width: column.width,
                      textAlign: column.align || "center"
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {masterclassTrainings?.map((row, index) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={row._id}>
                  <TableCell style={styles.tableRow} align="left">
                    <Typography fontSize="small">{row.day}</Typography>
                  </TableCell>
                  <TableCell style={styles.tableRow} align="left">
                    <AudioItem audio={row.audioFile} />
                  </TableCell>
                  <TableCell style={styles.tableRow} align="left">
                    <Typography fontSize="small">{row.title}</Typography>
                  </TableCell>
                  <TableCell style={styles.tableRow} align="right">
                    <Box display="flex" justifyContent="center" gap="10px">
                      <IconButton
                        onClick={() => {
                          setEditData(row);
                          setIsAddingMasterclassTraining(true);
                        }}
                        sx={{
                          height: "32px",
                          width: "32px",
                          backgroundColor: "#F3F4F6",
                          borderRadius: "4px"
                        }}
                        size="small"
                        aria-label="edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setConfirmDeleteModal(row._id);
                        }}
                        sx={{
                          height: "32px",
                          width: "32px",
                          backgroundColor: "#F3F4F6",
                          borderRadius: "4px"
                        }}
                        size="small"
                        aria-label="delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Users Section */}
      <Typography variant="h5" fontWeight={600} color="#374151" my={3}>
        Users
      </Typography>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={styles.tableContainer}>
          <Table stickyHeader aria-label="users table">
            <TableHead>
              <TableRow>
                {userColumns?.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      ...styles.headerCell,
                      width: column.width,
                      textAlign: column.align
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {users?.map((user) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={user._id}>
                  <TableCell style={styles.tableRow} align="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="left"
                      gap={2}
                    >
                      {user?.profilePic ? (
                        <ImageItem img={user?.profilePic} />
                      ) : (
                        <Avatar sx={{ width: 40, height: 40 }}>
                          {user.fullName
                            ? getInitials(user.fullName)
                            : getInitials(user.displayName)}
                        </Avatar>
                      )}
                      <Typography fontSize="small">
                        {user.fullName ? user.fullName : user.displayName}{" "}
                        {user.displayName ? `(${user.displayName})` : ""}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={styles.tableRow} align="center">
                    {user.email}
                  </TableCell>
                  <TableCell style={styles.tableRow} align="center">
                    <Chip
                      label={
                        getRoleChipStyle(
                          user.isAdmin === 1 ? user.userType : "user"
                        ).label
                      }
                      sx={
                        getRoleChipStyle(
                          user.isAdmin === 1 ? user.userType : "user"
                        ).style
                      }
                    />
                  </TableCell>
                  <TableCell style={styles.tableRow} align="center">
                    {user.trainingCompletions ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        flexDirection={"row"}
                        alignItems="center"
                        flexWrap="wrap"
                        gap={0.5} // small spacing between chips
                      >
                        {Object.values(user.trainingCompletions).map(
                          (value, index) => (
                            <Chip
                              key={index}
                              label={`${Math.round(value)}%`}
                              size="small"
                              sx={{
                                backgroundColor:
                                  parseFloat(value) >= 50
                                    ? "#4CAF50" // green for good progress
                                    : parseFloat(value) > 0
                                    ? "#FFC107" // amber for partial
                                    : "#F44336", // red for 0%
                                color: "#fff",
                                fontWeight: 700
                              }}
                            />
                          )
                        )}
                      </Box>
                    ) : (
                      "N/A"
                    )}
                  </TableCell>
                  <TableCell style={styles.tableRow} align="center">
                    {dayjs.utc(user.createdAt).format("MM/DD/YYYY")}
                  </TableCell>
                  <TableCell style={styles.tableRow} align="center">
                    {user.lastLogin
                      ? dayjs.utc(user.lastLogin).format("MM/DD/YYYY")
                      : "N/A"}
                  </TableCell>
                  <TableCell style={styles.tableRow} align="center">
                    <Box
                      display={"flex"}
                      gap="7px"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <StreaksIcon height={20} width={20} />
                      <Typography variant="bold">{user.streaks}</Typography>
                    </Box>
                  </TableCell>
                  {/* <TableCell sx={{ p: 0 }} align="center">
                    <BadgeDisplay open={user} />
                  </TableCell> */}
                  <TableCell align="center" style={styles.tableRow}>
                    <Typography
                      margin={0}
                      style={{
                        fontSize: "12px",
                        color: user.isActive ? "#2e7d32" : "#d32f2f",
                        backgroundColor: user.isActive ? "#e8f5e9" : "#ffebee",
                        padding: "5px",
                        width: "70%",
                        borderRadius: "5px"
                      }}
                    >
                      {user.isActive ? "Active" : "Inactive"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap="10px" justifyContent="center">
                      <IconButton
                        onClick={() => {
                          setIsModalOpen(user);
                        }}
                        sx={{
                          height: "32px",
                          width: "32px",
                          backgroundColor: "#F3F4F6",
                          borderRadius: "4px"
                        }}
                        size="small"
                        aria-label="diary"
                      >
                        <DiaryIcon style={{ height: "15px", width: "15px" }} />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          const customerDashboardUrl =
                            process.env.REACT_APP_STRIPE_PUBLIC_KEY?.includes(
                              "live"
                            )
                              ? process.env
                                  .REACT_APP_STRIPE_CUSTOMER_DASHBOARD_URL
                              : process.env
                                  .REACT_APP_TEST_STRIPE_CUSTOMER_DASHBOARD_URL;
                          const customerId = user?.stripeCustomerId;
                          window.open(
                            `${customerDashboardUrl + customerId}`,
                            "_blank"
                          );
                        }}
                        sx={{
                          height: "32px",
                          width: "32px",
                          backgroundColor: "#F3F4F6",
                          borderRadius: "4px"
                        }}
                        size="small"
                        aria-label="dollar"
                      >
                        <DollarIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={totalUsers}
          rowsPerPage={userRowsPerPage}
          page={userPage}
          onPageChange={handleUserChangePage}
          onRowsPerPageChange={handleUserChangeRowsPerPage}
        />
      </Paper>

      <NutritionTrackingModal
        open={!!isModalOpen}
        onClose={() => setIsModalOpen(null)}
        userData={isModalOpen}
      />
    </Box>
  );
};

export default MasterclassTrainingTable;
